import React from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Input } from '../../../components/ui/Input'
import { Button } from '../../../components/ui/Button'
import { apiFetch } from '../../../lib/api-client'

interface Regulation {
  id: string
  company_id: string
  title: string
  body: string
  sort_order: number
}

interface ReguleringsTabProps {
  companyId: string
}

export function ReguleringsTab({ companyId }: ReguleringsTabProps) {
  const [regs, setRegs] = React.useState<Regulation[]>([])
  const [loading, setLoading] = React.useState(true)
  const [adding, setAdding] = React.useState(false)
  const [newTitle, setNewTitle] = React.useState('')
  const [newBody, setNewBody] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [deleteError, setDeleteError] = React.useState<string | null>(null)

  React.useEffect(() => {
    apiFetch<Regulation[]>(`/api/v1/companies/${companyId}/regulations`)
      .then(setRegs)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [companyId])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newTitle.trim()) return
    setSaving(true)
    try {
      const created = await apiFetch<Regulation>(`/api/v1/companies/${companyId}/regulations`, {
        method: 'POST',
        body: JSON.stringify({
          title: newTitle.trim(),
          body: newBody.trim(),
          sort_order: regs.length,
        }),
      })
      setRegs((prev) => [...prev, created])
      setNewTitle('')
      setNewBody('')
      setAdding(false)
    } catch {
      // silent — form stays open for retry
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (regId: string) => {
    if (!window.confirm('Slet denne regulering?')) return
    setDeleteError(null)
    try {
      await apiFetch(`/api/v1/companies/${companyId}/regulations/${regId}`, { method: 'DELETE' })
      setRegs((prev) => prev.filter((r) => r.id !== regId))
    } catch {
      setDeleteError('Sletning mislykkedes. Prøv igen.')
    }
  }

  if (loading) {
    return <div style={{ padding: 28, color: '#94a3b8', fontSize: 13 }}>Henter…</div>
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 680 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>§11 Klausuler</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Brugerdefinerede klausuler der indsættes i §11 i lejekontrakter.
          </div>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus size={13} style={{ marginRight: 4 }} />
          Tilføj klausul
        </Button>
      </div>

      {adding && (
        <form onSubmit={handleAdd} style={{
          background: '#f8fafc', border: '1px solid #e8ecf4',
          borderRadius: 10, padding: 16, marginBottom: 16,
        }}>
          <div style={{ marginBottom: 12 }}>
            <Input
              label="Titel"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="f.eks. Husdyr ikke tilladt"
              autoFocus
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor="new-reg-body" style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
              Klausultekst
            </label>
            <textarea
              id="new-reg-body"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
              rows={4}
              placeholder="Skriv klausulteksten her…"
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 7,
                border: '1px solid #e8ecf4', fontSize: 13, color: '#0f172a',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="submit" size="sm" isLoading={saving}>Tilføj</Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setAdding(false)}>Annuller</Button>
          </div>
        </form>
      )}

      {deleteError && (
        <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{deleteError}</p>
      )}

      {regs.length === 0 ? (
        <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          Ingen klausuler endnu. Klik "Tilføj klausul" for at komme i gang.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {regs.map((reg) => (
            <RegCard
              key={reg.id}
              reg={reg}
              companyId={companyId}
              onDeleted={handleDelete}
              onUpdated={(updated) => setRegs((prev) => prev.map((r) => r.id === updated.id ? updated : r))}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function RegCard({ reg, companyId, onDeleted, onUpdated }: {
  reg: Regulation
  companyId: string
  onDeleted: (id: string) => void
  onUpdated: (updated: Regulation) => void
}) {
  const [editing, setEditing] = React.useState(false)
  const [title, setTitle] = React.useState(reg.title)
  const [body, setBody] = React.useState(reg.body)
  const [saving, setSaving] = React.useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await apiFetch<Regulation>(`/api/v1/companies/${companyId}/regulations/${reg.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title: title.trim(), body: body.trim() }),
      })
      onUpdated(updated)
      setEditing(false)
    } catch {
      // silent — keep editing open for retry
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 8, padding: 16 }}>
      {editing ? (
        <>
          <div style={{ marginBottom: 10 }}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} label="Titel" />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label htmlFor={`reg-body-${reg.id}`} style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
              Tekst
            </label>
            <textarea
              id={`reg-body-${reg.id}`}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              style={{
                width: '100%', padding: '8px 10px', borderRadius: 7,
                border: '1px solid #e8ecf4', fontSize: 13, color: '#0f172a',
                resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="sm" isLoading={saving} onClick={handleSave}>Gem</Button>
            <Button size="sm" variant="secondary" onClick={() => setEditing(false)}>Annuller</Button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a', marginBottom: 4 }}>{reg.title}</div>
            {reg.body && <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{reg.body}</div>}
          </div>
          <div style={{ display: 'flex', gap: 6, marginLeft: 12 }}>
            <button
              onClick={() => setEditing(true)}
              style={{ fontSize: 12, color: '#4f46e5', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px' }}
            >
              Rediger
            </button>
            <button
              onClick={() => onDeleted(reg.id)}
              style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
